import React, { useLayoutEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import HotelLoader from './hotel/HotelLoader.react';
import HotelCardComponent from './hotel/HotelCardComponent.react';
import { hotelElementsStore } from './HotelElementsStore';
import ResultsStore from '../../../stores/ResultsStore';
import SearchStore from '../../../stores/SearchStore';
import ProfileStore from '../../../stores/ProfileStore';
import Analytics from 'arbitrip-common/client/analytics';
import Config from 'arbitrip-common/client/utils/Config';
import {
    MAX_LIST_ITEMS,
    getTimestampMillis,
    buildListVersion,
    createListVersionTracker,
    shouldReportRenderedList,
    createRenderedCardsTracker,
    createSessionWaitTimeTracker,
    buildHotelsResultsItems,
    buildHotelsResultsDestination,
} from 'arbitrip-common/client/analytics/services/listVersionService';

// Module scope so returning from a hotel page does not report the search wait again.
const listWaitTimeTracker = createSessionWaitTimeTracker();

// Virtuoso swaps card content after the list's own componentDidUpdate, so each card reports once committed.
const CommittedCardReporter = ({ index, hotelId, onCommit, onRemove, children }) => {
    useLayoutEffect(() => {
        onCommit(index, hotelId);
        return () => onRemove(index, hotelId);
    }, [index, hotelId, onCommit, onRemove]);

    return children;
};

// Neighborhood keys are auto-added (as true) while results stream in; only user exclusions define the list.
const getListVersionFilters = () => {
    const filters = ResultsStore.getFilters();
    const neighborhoods = filters.neighborhoods || {};
    return {
        ...filters,
        neighborhoods: Object.keys(neighborhoods).filter((key) => !neighborhoods[key]).sort(),
    };
};

class HotelList extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {};
        this._listVersionTracker = createListVersionTracker();
        this._renderedCards = createRenderedCardsTracker();
        this._isReportScheduled = false;
        this._isUnmounted = false;
        this._onResultsChange = this._onResultsChange.bind(this);
        this._onCardCommitted = this._onCardCommitted.bind(this);
        this._onCardRemoved = this._onCardRemoved.bind(this);
    }

    componentDidMount() {
        this._unsubscribe = hotelElementsStore.subscribe(() => this.forceUpdate());
        hotelElementsStore._onStoreChange();
        ResultsStore.addChangeListener(this._onResultsChange);
        this._scheduleReport();
    }

    componentDidUpdate() {
        this._scheduleReport();
    }

    componentWillUnmount() {
        this._isUnmounted = true;
        if (this._unsubscribe) {
            this._unsubscribe();
        }
        ResultsStore.removeChangeListener(this._onResultsChange);
    }

    _getListVersion() {
        return buildListVersion({
            searchToken: this.props.searchToken,
            searchSessionId: getTimestampMillis(this.props.searchSessionCreatedTimestamp),
            sort: ResultsStore.getSortMethod(),
            filters: getListVersionFilters(),
        });
    }

    // hotelElementsStore skips same-count filter changes, so rerender on a new list version.
    _onResultsChange() {
        try {
            const listVersion = this._getListVersion();
            if (listVersion !== this.state.listVersion) {
                this.setState({ listVersion });
            }
        } catch (err) {
            console.error('HotelList::: failed to read hotels list version', err);
        }
    }

    _onCardCommitted(index, hotelId) {
        this._renderedCards.commit(index, hotelId);
        this._scheduleReport();
    }

    _onCardRemoved(index, hotelId) {
        this._renderedCards.remove(index, hotelId);
        this._scheduleReport();
    }

    // Deferred past the current commit so every card of a list swap has reported first.
    _scheduleReport() {
        // Web sent no list view before the ELAL contract, so other brands keep sending none.
        if (!Config.is_elal || this._isReportScheduled) {
            return;
        }

        this._isReportScheduled = true;
        Promise.resolve().then(() => {
            this._isReportScheduled = false;
            if (!this._isUnmounted) {
                this._reportRenderedList();
            }
        });
    }

    _reportRenderedList() {
        try {
            const { showLoaders, completed_listening, breakfast_only, searchToken, searchSessionCreatedTimestamp } =
                this.props;
            const listCount = hotelElementsStore.getCount();
            const shouldReport = shouldReportRenderedList({
                tracker: this._listVersionTracker,
                version: this._getListVersion(),
                renderedCount: this._renderedCards.countMatching((index) => hotelElementsStore.getItem(index)?.id),
                isLoading: showLoaders,
                isCompleted: completed_listening,
            });
            if (!shouldReport) {
                return;
            }

            const hotels = Array.from({ length: Math.min(listCount, MAX_LIST_ITEMS) }, (_, index) =>
                hotelElementsStore.getItem(index),
            );
            const search = SearchStore.getSearchTerms();
            const profile = ProfileStore.getProfile();
            Analytics.actions.responses.hotelsResults(
                listCount,
                listWaitTimeTracker.measure({ searchToken, startedAt: searchSessionCreatedTimestamp }),
                {
                    user: profile,
                    destination: buildHotelsResultsDestination(search?.destination),
                    search,
                    hotels: buildHotelsResultsItems(hotels, search, {
                        breakfastOnly: !!breakfast_only,
                        displayCurrency: profile?.display_currency,
                        displayExchangeRate: profile?.display_currency_exchange_rate,
                    }),
                },
            );
        } catch (err) {
            console.error('HotelList::: failed to report hotels list view', err);
        }
    }

    render() {
        const { showLoaders } = this.props;
        const {
            profile,
            searchToken,
            searchTerms,
            completed_hotels,
            completed_search,
            completed_listening,
            breakfast_only,
            completed_delay,
            arbitrip_points_applied,
            amenities_data,
            private_travel,
            contract_client_company_id,
        } = this.props;

        return (
            <Virtuoso
                useWindowScroll
                totalCount={showLoaders ? 10 : hotelElementsStore.getCount()}
                itemContent={(index) => {
                    if (showLoaders) {
                        return <HotelLoader hotel={{ id: index }} />;
                    }
                    const hotel = hotelElementsStore.getItem(index);
                    if (!hotel) return null;
                    return (
                        <CommittedCardReporter
                            index={index}
                            hotelId={hotel.id}
                            onCommit={this._onCardCommitted}
                            onRemove={this._onCardRemoved}
                        >
                            <HotelCardComponent
                                hotel={hotel}
                                key={'hotel-key-' + hotel.id}
                                searchToken={searchToken}
                                searchTerms={searchTerms}
                                profile={profile}
                                enableDealLoader={!!(completed_hotels || completed_search)}
                                completedListening={!!completed_listening}
                                breakfastOnly={!!breakfast_only}
                                forceDealLoader={!completed_delay}
                                arbitripPointsApplied={!!arbitrip_points_applied}
                                amenitiesData={amenities_data}
                                sortPosition={index + 1}
                                privateTravel={!!private_travel}
                                contract_client_company_id={contract_client_company_id}
                            />
                        </CommittedCardReporter>
                    );
                }}
            />
        );
    }
}

export default HotelList;
